import React, { useState, useEffect, useRef } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
// import DownloadVideo from "./DownloadVideo";
import "../Download/DownloadSidebar.css";

function DownloadButton({
  wo,
  ImgUri,
  setDownloadProgress,
  downloadProgress,
  setArchivedImages,
  downloadImages,
  setDownloadImages,
  setIsCheckAll,
  isCheckAll,
  setIsCheck,
  isCheck,
  setIsDownloadSidebarVisible,
  videoSasToken = "",
  nonVideoSasToken = "",
  createDialogBox,
  closeDialogBox,
  fetchVideoSasToken,
  fetchNonVideoSasToken,
}) {
  // refs used for immediate, synchronous guards against multiple dialog/actions
  const authDialogShownRef = useRef(false);
  const extendingSessionRef = useRef(false);

  // state-driven indicator used to disable UI while tokens refresh
  const [isRefreshingTokens, setIsRefreshingTokens] = useState(false);

  const handleAuthFailure = () => {
    // Prevent dialog during an existing dialog or while we're already refreshing tokens
    if (authDialogShownRef.current || isRefreshingTokens) return;
    authDialogShownRef.current = true;

    if (createDialogBox && closeDialogBox) {
      createDialogBox(
        "Session expired",
        "Your session has expired due to inactivity.",
        [
          {
            type: "button",
            text: "Extend session",
            attributes: { id: "dialog-box-extend-button" },
            listener: {
            event: "click",
            callback: async () => {
              // guard against repeated clicks
              if (extendingSessionRef.current) return;
              extendingSessionRef.current = true;
              setIsRefreshingTokens(true);
              if (closeDialogBox) closeDialogBox();                            
              // Clear all failed downloads before refreshing tokens
              setDownloadProgress((prev) => {
                const updated = { ...prev };
                Object.keys(updated).forEach((key) => {
                  if (updated[key]?.status === "failed") {
                    delete updated[key];
                  }
                });
                return updated;
              });

              // Fetch new tokens from parent
              await fetchVideoSasToken();
              await fetchNonVideoSasToken();
              
              // Wait for parent to update token props
              await new Promise((res) => setTimeout(res, 1000));
              
              // Reset flags after successful token refresh
              authDialogShownRef.current = false;
              extendingSessionRef.current = false;
              setIsRefreshingTokens(false);
            },
            },
          },
        ],
        () => {
          authDialogShownRef.current = false; // just in case
          setIsRefreshingTokens(false);
          window.location.reload(); // Refresh page on dialog close to reset state and prompt login
      }
      );
    }
  };
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentDownloadList, setCurrentDownloadList] = useState([]);
  const [filesDownloaded, setFilesDownloaded] = useState(0);
  const [isCreatingZip, setIsCreatingZip] = useState(false);
  const [failedFiles, setFailedFiles] = useState([]); // Add this
  const failedFilesRef = useRef([]);

  // keep a reference to the list that was being downloaded when the
  // operation started so that retries can still access it even if the
  // user selection is cleared later.
  const prevDownloadListRef = useRef([]);

  useEffect(() => {
    if (Object.keys(downloadProgress).length === 0) {
      setFailedFiles([]);
    }
  }, [downloadProgress]);

  useEffect(() => {
    setCurrentDownloadList(downloadImages || []);
  }, [isCheckAll, ImgUri, downloadImages]);

  const setProgress = (key, updates) =>
    setDownloadProgress((prev) => ({
      ...prev,
      [key]: {
        ...(typeof prev[key] === "object" ? prev[key] : {}),
        ...updates,
      },
    }));

  const markFailed = (key, error) => {
  setProgress(key, {
    status: "failed",
    error: (error && error.message) ? error.message : String(error || "Failed"),
    percent: null,
  });
  // Add failed file to list
  setFailedFiles((prev) => {
    if (!prev.includes(key)) {
      const next = [...prev, key];
      failedFilesRef.current = next;
      return next;
    }
    return prev;
  });
};

  const markStarted = (key, fileInfo = {}) =>
    setProgress(key, { status: "in-progress", percent: 0, ...fileInfo });

  const markCompleted = (key) => {
  setProgress(key, { status: "completed", percent: 100 });
  // Remove from failedFiles if it was retried successfully
  setFailedFiles((prev) => prev.filter((fileurl) => fileurl !== key));
};

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const VIDEO_EXTENSIONS = ["mp4", "avi", "mov", "mkv", "flv", "wmv", "webm", "m4v", "hevc"];
  const isVideoFile = (ext) => !!(ext && VIDEO_EXTENSIONS.includes(ext.toLowerCase()));
  const LARGE_THRESHOLD = 100 * 1024 * 1024; // 100 MB
  const ZIP_CHUNK_THRESHOLD = 1024 * 1024 * 1024; // ~1 GB
  const SELECTION_SIZE_THRESHOLD = 10; // if <=10, download all individually

  const appendSasTokenToUrl = (url, ext = "") => {
    if (!url) return url;
    const token = isVideoFile(ext) ? videoSasToken : nonVideoSasToken;
    if (!token) return url;
    return `${url}${token}`;
  };

  const parseImage = (item) => {
    const s = typeof item?.image === "string" ? item.image : (typeof item === "string" ? item : "");
    const [fileurl = "", fileName = "download", ext = "", fileSize = ""] = s.split("$");
    const fileSizeNum = (() => {
      if (fileSize === undefined || fileSize === null || fileSize === "") return null;
      const n = Number(fileSize);
      return Number.isFinite(n) && n >= 0 ? n : null;
    })();
    return { fileurl, fileName, ext, fileSize, fileSizeNum };
  };

  const downloadFile = async (fileurl, downloadFileName, downloadFileNameExtension) => {
    const res = await fetch(fileurl, { mode: "cors" });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = downloadFileName.endsWith(`.${downloadFileNameExtension}`)
      ? downloadFileName
      : `${downloadFileName}.${downloadFileNameExtension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const processIndividualDownload = async (item) => {
    const { fileurl, fileName, ext } = parseImage(item);
    if (!fileurl) {
      markFailed(fileurl || item, new Error("Missing file URL"));
      return;
    }
    // choose URL for fetching: use correct SAS token based on file type
    const fetchUrl = appendSasTokenToUrl(fileurl, ext);
    try {
      //NOTE: For simplicity, we're treating all files the same with the same downloadFile function with (SAS Token).
      // if (isVideoFile(ext)) {
      //   // use original DownloadVideo signature for videos when downloading individually
      //   await DownloadVideo(fileName, ext, fileurl, (progress) => {
      //     setProgress(fileurl, { percent: progress, status: "in-progress" });
      //   });
      // } else {
      //   await downloadFile(fetchUrl, fileName, ext);
      // }
      await downloadFile(fetchUrl, fileName, ext);
      markCompleted(fileurl);
      setFilesDownloaded((n) => n + 1);
    } catch (err) {
      // if we detected auth error from message
      const msg = err?.message || "";
      if (msg.includes("HTTP 401") || msg.includes("HTTP 403")) {
        handleAuthFailure();
      }
      markFailed(fileurl, err);
      setFilesDownloaded((n) => n + 1);
    }
  };

  const startParallelDownloads = (filesToDownload) => {
    const CONCURRENCY = 3;
    let idx = 0;
    const workers = new Array(Math.min(CONCURRENCY, filesToDownload.length))
      .fill(null)
      .map(async () => {
        while (idx < filesToDownload.length) {
          const i = idx++;
          await processIndividualDownload(filesToDownload[i]);
          await sleep(50);
        }
      });
    return Promise.all(workers);
  };

  const handleRetryFailed = async () => {
    if (failedFiles.length === 0) return;

    const failedFilesBeforeRetry = [...failedFiles];
    // compute the items we would retry *before* toggling any UI state
    const failedItems = prevDownloadListRef.current.filter((item) => {
      const { fileurl } = parseImage(item);
      return failedFilesBeforeRetry.includes(fileurl);
    });

    if (failedItems.length === 0) return;

    setIsDownloading(true);
    setFilesDownloaded(0);
    setIsDownloadSidebarVisible(true);

    try {
      // Clear previous failed status
      failedItems.forEach((item) => {
        const { fileurl, fileName, ext, fileSize } = parseImage(item);
        markStarted(fileurl, { fileName, ext, fileSize });
      });

      const retryAllIndividually = failedItems.length <= SELECTION_SIZE_THRESHOLD;
      if (retryAllIndividually) {
        await startParallelDownloads(failedItems);
      } else {
        // Keep the same behavior as main download: >10 files -> ZIP candidates,
        // while files >100MB continue as individual downloads.
        const largeFiles = [];
        const zipCandidates = [];
        failedItems.forEach((item) => {
          const { fileSizeNum } = parseImage(item);
          if (fileSizeNum !== null && fileSizeNum > LARGE_THRESHOLD) {
            largeFiles.push(item);
          } else {
            zipCandidates.push(item);
          }
        });

        const largeDownloadsPromise = startParallelDownloads(largeFiles);

        if (zipCandidates.length > 0) {
          setIsCreatingZip(true);
          const filenames = new Set();
          let zip = new JSZip();
          let currentChunkSize = 0;
          let chunkIndex = 1;

          const addFileToZip = async (item) => {
            const { fileurl, fileName, ext, fileSizeNum } = parseImage(item);
            try {
              const downloadUrl = appendSasTokenToUrl(fileurl, ext);
              const res = await fetch(downloadUrl, { mode: "cors" });
              if (!res.ok) {
                if (res.status === 401 || res.status === 403) {
                  handleAuthFailure();
                }
                throw new Error(`HTTP ${res.status}`);
              }
              const blob = await res.blob();

              const extension = ext || "";
              let baseName = fileName || "download";
              let uniqueName = baseName;
              let counter = 1;
              while (filenames.has(uniqueName + (extension ? "." + extension : ""))) {
                uniqueName = `${baseName}(${counter})`;
                counter++;
              }
              const fullName = extension ? `${uniqueName}.${extension}` : uniqueName;
              filenames.add(fullName);

              zip.file(fullName, blob);
              currentChunkSize += blob.size || (fileSizeNum || 0);
              setProgress(fileurl, { status: "added-to-zip", percent: null });
              markCompleted(fileurl);
              setFilesDownloaded((n) => n + 1);
            } catch (err) {
              markFailed(fileurl, err);
              setFilesDownloaded((n) => n + 1);
            }
          };

          const BATCH_SIZE = 10;
          for (let i = 0; i < zipCandidates.length; i += BATCH_SIZE) {
            const batch = zipCandidates.slice(i, i + BATCH_SIZE);
            await Promise.all(batch.map((item) => addFileToZip(item)));

            const nextItem = zipCandidates[i + BATCH_SIZE];
            const nextSize = nextItem ? parseImage(nextItem).fileSizeNum || 0 : 0;

            if (currentChunkSize + nextSize > ZIP_CHUNK_THRESHOLD && currentChunkSize > 0) {
              try {
                const content = await zip.generateAsync({
                  type: "blob",
                  compression: "STORE",
                  streamFiles: true,
                });
                saveAs(content, `WorkOrder_${wo}_retry_part${chunkIndex}.zip`);
              } catch (err) {
                console.error("Error finalizing retry zip part:", err);
              }
              zip = new JSZip();
              currentChunkSize = 0;
              chunkIndex++;
            }
          }

          if (currentChunkSize > 0) {
            try {
              const content = await zip.generateAsync({
                type: "blob",
                compression: "STORE",
                streamFiles: true,
              });
              saveAs(content, `WorkOrder_${wo}_retry_part${chunkIndex}.zip`);
            } catch (err) {
              console.error("Error finalizing last retry zip part:", err);
            }
          }
          setIsCreatingZip(false);
        }

        await largeDownloadsPromise;
      }
      // Note: failedFiles state is already managed by markCompleted/markFailed during retry
    } catch (err) {
      console.error("Error during retry:", err);
    } finally {
      setIsDownloading(false);
      setIsCreatingZip(false);
      // clear any lingering selection as retry concludes
      setIsCheckAll(false);
      setDownloadImages([]);
      setArchivedImages([]);
      setIsCheck([]);

      // update prev list to whatever still failed (if any) so a further retry
      // can proceed.  the failedFiles state/ref is already up to date.
      if (failedFilesRef.current.length > 0) {
        prevDownloadListRef.current = prevDownloadListRef.current.filter((item) => {
          const { fileurl } = parseImage(item);
          return failedFilesRef.current.includes(fileurl);
        });
      } else {
        prevDownloadListRef.current = [];
      }
      // Do NOT clear downloadImages and isCheck here as retry is a secondary operation
      // These should only be cleared on main download completion
    }
  };

  const handleDownload = async () => {
    if (!currentDownloadList || currentDownloadList.length === 0) return;

    // stash the list we are about to download so retries can still access it
    prevDownloadListRef.current = currentDownloadList;

    setIsDownloading(true);
    setFilesDownloaded(0);
    setIsDownloadSidebarVisible(true);

    const filenames = new Set();

    try {
      currentDownloadList.forEach((item) => {
        const { fileurl, fileName, ext, fileSize } = parseImage(item);
        if (fileurl) markStarted(fileurl, { fileName, ext, fileSize });
      });

      // Classification rule: size-only (>100MB => individual). Missing size => ZIP-candidate.
      const largeFiles = [];
      const zipCandidates = [];
      currentDownloadList.forEach((item) => {
        const { fileSizeNum } = parseImage(item);
        if (fileSizeNum !== null && fileSizeNum > LARGE_THRESHOLD) {
          largeFiles.push(item);
        } else {
          zipCandidates.push(item);
        }
      });

      const downloadAllIndividually = currentDownloadList.length <= SELECTION_SIZE_THRESHOLD;

      if (downloadAllIndividually) {
        // <=10 items: download each individually. Videos use DownloadVideo.
        await startParallelDownloads(currentDownloadList);
      } else {
        // >10 items:
        // - files >100MB download individually in parallel (videos use DownloadVideo)
        // - remaining files (including videos <=100MB) are zipped sequentially
        const largeDownloadsPromise = startParallelDownloads(largeFiles);
             
        // Parallel file fetching without compression
        if (zipCandidates.length > 0) {
          setIsCreatingZip(true);
          let zip = new JSZip();
          let currentChunkSize = 0;
          let chunkIndex = 1;

          const addFileToZip = async (item) => {
            const { fileurl, fileName, ext, fileSizeNum } = parseImage(item);
            try {
              let blob;
              // if(isVideoFile(ext)) {
              //   const apiurl = process.env.REACT_APP_VIDEO_GETBLOB_API_URL;
              //   const blobURLSplit = fileurl.split("/");
              //   let blobName = blobURLSplit[blobURLSplit.length - 1];
              //   let fileExtension = ext;
              //   const response = await axios.get(apiurl, {
              //     params: { blobName, fileExtension },
              //     responseType: 'blob',
              //     onDownloadProgress: (progressEvent) => {
              //       const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              //       setProgress(fileurl, { percent: percentage, status: "in-progress" });
              //     }
              //   });
              //   blob = response.data;
              // } else {
                const downloadUrl = appendSasTokenToUrl(fileurl, ext);
                const res = await fetch(downloadUrl, { mode: "cors" });
                if (!res.ok) {
                  if (res.status === 401 || res.status === 403) {
                    handleAuthFailure();
                  }
                  throw new Error(`HTTP ${res.status}`);
                }
                blob = await res.blob();
              // }
              
              const extension = ext || "";
              let baseName = fileName || "download";
              let uniqueName = baseName;
              let counter = 1;
              while (filenames.has(uniqueName + (extension ? "." + extension : ""))) {
                uniqueName = `${baseName}(${counter})`;
                counter++;
              }
              const fullName = extension ? `${uniqueName}.${extension}` : uniqueName;
              filenames.add(fullName);

              zip.file(fullName, blob);
              currentChunkSize += blob.size || (fileSizeNum || 0);
              setProgress(fileurl, { status: "added-to-zip", percent: null });
              markCompleted(fileurl);
              setFilesDownloaded((n) => n + 1);
            } catch (err) {
              const { fileurl } = parseImage(item);
              markFailed(fileurl, err);
              setFilesDownloaded((n) => n + 1);
            }
          };

          // Process files in smaller batches
          const BATCH_SIZE = 10;
          for (let i = 0; i < zipCandidates.length; i += BATCH_SIZE) {
            const batch = zipCandidates.slice(i, i + BATCH_SIZE);
            
            await Promise.all(batch.map(item => addFileToZip(item)));
            
            const nextItem = zipCandidates[i + BATCH_SIZE];
            const nextSize = nextItem ? parseImage(nextItem).fileSizeNum || 0 : 0;
            
            if (currentChunkSize + nextSize > ZIP_CHUNK_THRESHOLD && currentChunkSize > 0) {
              try {
                const content = await zip.generateAsync({
                  type: "blob",
                  compression: "STORE",
                  // compressionOptions: { level: 1 },
                  streamFiles: true,
                });
                saveAs(content, `WorkOrder_${wo}_part${chunkIndex}.zip`);
              } catch (err) {
                console.error("Error finalizing zip part:", err);
              }
              zip = new JSZip();
              currentChunkSize = 0;
              chunkIndex++;
            }
          }

          // Final chunk
          if (currentChunkSize > 0) {
            try {
              const content = await zip.generateAsync({
                type: "blob",
                compression: "STORE",
                // compressionOptions: { level: 1 },
                streamFiles: true,
              });
              saveAs(content, `WorkOrder_${wo}_part${chunkIndex}.zip`);
            } catch (err) {
              console.error("Error finalizing last zip part:", err);
            }
          }
          setIsCreatingZip(false);
        }

        await largeDownloadsPromise;
      }
    } finally {
      setIsDownloading(false);
      setIsCreatingZip(false);
      setIsCheckAll(false);
      setDownloadImages([]);
      setArchivedImages([]);
      setIsCheck([]);

      // prepare retry list: keep only failed items in the ref, if any
      if (failedFilesRef.current.length > 0) {
        prevDownloadListRef.current = prevDownloadListRef.current.filter((item) => {
          const { fileurl } = parseImage(item);
          return failedFilesRef.current.includes(fileurl);
        });
      } else {
        prevDownloadListRef.current = [];
      }
    }
  };

  const buttonText = (() => {
    const count = currentDownloadList.length;
    return count > 10 ? `Download Zip (${count})` : count > 0 ? `Download (${count})` : "Download";
  })();

  const listLength = currentDownloadList.length;
  const tippymsg = `Your download is underway. Attempting download: (${filesDownloaded}/${listLength}).`;

  const isBtnDisabled = isDownloading || !currentDownloadList.length || isCheck.length === 0 || isRefreshingTokens;

  return (
    <>
      <Tippy
        content={
          <>
            <strong>Note:</strong> The download time may vary based on your internet connection
            speed, especially for larger files. Please avoid refreshing the page while waiting.
          </>
        }
        placement="bottom"
        theme="custom"
      >
        <button
          className={`btn-download ${isDownloading ? "is-downloading" : ""}`}
          onClick={handleDownload}
          disabled={isBtnDisabled}
        >
          {isCreatingZip ? (
            <Tippy content={tippymsg} placement="bottom" theme="custom">
            <span>Creating Zip...</span>
            </Tippy>
          ) : isDownloading ? (
            <Tippy content={tippymsg} placement="bottom" theme="custom">
              <span>Downloading...</span>
            </Tippy>
          ) : isBtnDisabled ? (
            "Download"
          ) : (
            buttonText
          )}
        </button>
      </Tippy>

      {failedFiles.length > 0 && !isDownloading && (
        <button
          className="btn-retry-failed"
          onClick={handleRetryFailed}
          title={`Retry ${failedFiles.length} failed file(s)`}
        >
          Retry Failed ({failedFiles.length})
        </button>
      )}
    </>
  );
}

export default DownloadButton;
